import { JsonParam, QueryParamConfig } from "use-query-params";

/**
 * JsonParam that omits empty arrays from the URL.
 * When the value is an empty array, it returns undefined,
 * which causes use-query-params to remove it from the URL.
 *
 * This prevents URLs like:
 * - /experiments?filters=%5B%5D&groups=%5B%5D&size=100
 *
 * Instead producing clean URLs like:
 * - /experiments?size=100
 */
export const JsonParamOmitEmptyArray = <T extends unknown[]>(
  defaultValue: T = [] as unknown as T,
): QueryParamConfig<T | undefined, T> => {
  return {
    ...JsonParam,
    encode: (value: T | null | undefined): string | undefined => {
      // If value is empty array, null, or undefined, return undefined to omit from URL
      // This prevents filters=%5B%5D and groups=%5B%5D from appearing in URLs
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return undefined;
      }
      return JsonParam.encode(value as unknown) as string | undefined;
    },
    decode: (value: string | (string | null)[] | null | undefined): T => {
      // Handle missing or empty query param
      if (value === null || value === undefined || value === "") {
        return defaultValue;
      }
      // Decode the JSON value - JsonParam.decode expects string | undefined
      const stringValue =
        typeof value === "string" ? value : Array.isArray(value) ? value[0] ?? "" : "";
      if (!stringValue) {
        return defaultValue;
      }
      const decoded = JsonParam.decode(stringValue);
      // If decoded is an empty array (e.g., from existing URL with filters=%5B%5D),
      // return default and let encode() clean it up on next render
      if (Array.isArray(decoded) && decoded.length === 0) {
        return defaultValue;
      }
      return (decoded as T) ?? defaultValue;
    },
  };
};
